import { create } from 'zustand';
import { apiClient } from '@/api/client';

/**
 * Phase 26 — shortcode-based sticker referencing.
 *
 * Когда пользователь кликает стикер в пикере, в инпут вставляется `:<name>:`
 * где name — basename URL-а стикера без расширения (`/uploads/stickers/<packId>/<stickerId>.png`
 * → `<stickerId>`). При рендере сообщения MessageItem парсит `:([\w-]+):`
 * и подставляет inline-картинку.
 *
 * Кэш строится лениво при первом обращении: грузим публичные паки + мои,
 * собираем name→url. Если в сообщении упомянут shortcode, которого нет
 * в кэше, — оставляем как текст.
 */
interface StickerCacheState {
  byName: Record<string, string>; // shortcode → absolute URL path (as served)
  loaded: boolean;
  loading: boolean;
  ensure: () => Promise<void>;
}

const apiBase = ((import.meta as any).env?.VITE_API_URL ?? '').replace(/\/$/, '');

function resolveStickerUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/')) return `${apiBase}${url}`;
  return url;
}

export function urlToShortcode(url: string): string {
  // /uploads/stickers/<packId>/<stickerId>.png → <stickerId>
  const m = url.match(/\/([^/]+?)(?:\.[a-z0-9]+)?$/i);
  return m ? m[1] : url;
}

export const useStickerCacheStore = create<StickerCacheState>((set, get) => ({
  byName: {},
  loaded: false,
  loading: false,
  ensure: async () => {
    const s = get();
    if (s.loaded || s.loading) return;
    set({ loading: true });
    try {
      const [pub, mine] = await Promise.all([
        apiClient.get<Array<{ id: string }>>('/api/stickers/packs').catch(() => ({ data: [] })),
        apiClient.get<Array<{ id: string }>>('/api/stickers/me').catch(() => ({ data: [] })),
      ]);
      const packIds = Array.from(
        new Set([...(pub.data ?? []).map((p) => p.id), ...(mine.data ?? []).map((p) => p.id)]),
      );
      const next: Record<string, string> = {};
      // Fetch stickers for each pack in parallel
      const packResponses = await Promise.all(
        packIds.map((id) =>
          apiClient
            .get<{ stickers: Array<{ url: string }> }>(`/api/stickers/packs/${id}`)
            .then((r) => r.data.stickers ?? [])
            .catch(() => [] as Array<{ url: string }>),
        ),
      );
      for (const stickers of packResponses) {
        for (const st of stickers) {
          if (!st.url) continue;
          next[urlToShortcode(st.url)] = resolveStickerUrl(st.url);
        }
      }
      set({ byName: next, loaded: true, loading: false });
    } catch {
      set({ loading: false });
    }
  },
}));
